import type { CodexSessionExt, Session } from '@orgus/protocol';
import { agentCapabilities, agentKindLabel, agentKindOf, agentSupports, clip, runtimeOf, sessionOrigin } from '@orgus/protocol';
import { Activity, ArrowUpRight, Bot, ChevronDown, ChevronUp, Copy, FileDiff, FileText, ListTree, Octagon, SearchX, SquareTerminal } from '@orgus/ui/icons';
import { useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Button,
  ConfirmDialog,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  RelativeTime,
  SkeletonText,
  StatusDot,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  formatCompact,
  formatInt,
  formatMoney,
  formatMoneyCompact,
  plural,
  shortPath,
  toast,
} from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { runAction, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useCheckout, useConnection, useMachine, useProject, useSession } from '../../lib/store.ts';
import { useTerms } from '../terminal/api.ts';
import { termForSession } from '../terminal/model.ts';
import { SessionTerminalTab } from '../terminal/SessionTerminalTab.tsx';
import { sessionRanMs } from './duration.ts';
import { isLive, resumeCommand } from './labels.ts';
import { SessionLanes } from './SessionLanes.tsx';
import { AgentKindLabel } from '../adapters/AgentKind.tsx';
import { CapabilityList, capabilitySummary, unavailableReason } from '../adapters/Capabilities.tsx';
import { livenessGuessNote, sessionStateOfPill } from '../adapters/state.ts';
import { PARTIAL_HINT, agentRowCount, agentsShownNote, compactLowerBound, lowerBound, subagentTotal, workflowTotal } from './counts.ts';
import { Transcript } from './Transcript.tsx';
import { FailureCard } from './FailureCard.tsx';
import { SessionApprovals } from '../approvals/SessionApprovals.tsx';
import { ApprovalBlocker } from '../approvals/ApprovalBlocker.tsx';
import { ApprovalEvents } from '../approvals/ApprovalExpiredEvent.tsx';
import { SessionBlockedBadge } from '../approvals/SessionBlockedBadge.tsx';
import { SessionMessage } from '../control/SessionControl.tsx';
import { useControlState } from '../control/live.ts';
import { SessionAutonomy } from '../autonomy/SessionAutonomy.tsx';
import { SessionClaimButton } from '../autonomy/SessionClaimButton.tsx';
import { claimAction } from '../autonomy/model.ts';
import { useClaim, useMe, useMessages, useSessionDetail } from '../session-detail/api.ts';
import { useTeams } from '../teams/model.ts';
import { useSessionCost } from '../costs/useCosts.ts';
import { FACT_CELL, FACT_GRID, NoValue, SessionFacts, SessionSummaryLine } from './SessionFacts.tsx';
import { FolderValue } from './FolderFact.tsx';
import { DetailSection, FactGrid } from '../detail/FactGrid.tsx';
import { useSessionParam } from './useSessionParam.ts';
import { MissionChip, useLaunchModel, useSessionTitle } from './title.tsx';
import { PresenceAvatars } from '../teams/PresenceAvatars.tsx';
import { SessionOwner } from '../teams/SessionOwner.tsx';
import { OutOfScopeState, useOutOfScope } from '../teams/OutOfScope.tsx';
import { CostTab } from '../session-detail/CostTab.tsx';
import { UsageGroup } from '../session-detail/UsageGroup.tsx';
import { DiffTab } from '../session-detail/DiffTab.tsx';
import { useMissionsState } from '../missions/data.ts';
import { incidentPath } from '../watch/model.ts';
import { missionIdOfTitle } from './shownTitle.ts';
import { EventsTab } from '../session-detail/EventsTab.tsx';
import { Messages } from '../session-detail/Messages.tsx';
import { MessageEntry } from '../session-detail/MessageEntry.tsx';
import { useApprovalBlock } from '../approvals/blocked.ts';
import { drawerWidth, useDiffMode, useSessionControl } from '../session-detail/DetailTabs.tsx';
import { SessionModelText, WaitingCallout, useAskView } from '../session-detail/Waiting.tsx';
import { useSessionNeedsLevel } from '../asks/needsYou.ts';
import { ASKED_LABEL, OPEN_QUESTION_LABEL, pillLabelForLevel } from '../asks/levels.ts';

const PILL: Record<Session['status'], PillStatus> = { waiting: 'needs-you', working: 'working', idle: 'idle', ended: 'ended' };
const PILL_LABEL: Partial<Record<Session['status'], string>> = { waiting: 'Needs you' };

/**
 * Each tab's content is its own scroll container: the header, the four numbers and the tab
 * bar above it stay put, and only what the tab shows scrolls.
 */
const TAB_SCROLL = 'min-h-0 overflow-y-auto overscroll-contain';

type Tab = 'activity' | 'diff' | 'events' | 'agents' | 'details' | 'terminal';
const TABS: Tab[] = ['activity', 'diff', 'events', 'agents', 'details', 'terminal'];
/** The tabs a phone folds into More, unless one of them is open. */
const FOLDED = ['agents', 'details', 'terminal'] as const;
const FOLDED_LABEL: Record<(typeof FOLDED)[number], string> = { agents: 'Agents', details: 'Details', terminal: 'Terminal' };
const FOLDED_ICON: Record<(typeof FOLDED)[number], ReactNode> = { agents: <Bot />, details: <FileText />, terminal: <SquareTerminal /> };
const folded = (open: Tab, t: Tab) => (open === t ? undefined : 'max-sm:hidden');
/** Older links: the transcript is on Activity, the cost breakdown on Details. */
const TAB_ALIAS: Record<string, Tab> = { transcript: 'activity', cost: 'details' };

/** The drawer's tab, in the address (`?tab=`), so a link can open a session on its terminal or its diff. */
function useTab(): [Tab, (t: Tab) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') ?? '';
  const tab = (TABS as string[]).includes(raw) ? (raw as Tab) : (TAB_ALIAS[raw] ?? 'activity');
  const set = (t: Tab) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (t === 'activity') n.delete('tab');
        else n.set('tab', t);
        return n;
      },
      { replace: true, preventScrollReset: true },
    );
  return [tab, set];
}

/** The agent and its version: the session's own (Codex writes it), else what the machine has installed. */
function agentVersion(s: Session, machine: ReturnType<typeof useMachine>): string | null {
  const own = agentKindOf(s) === 'codex' ? (s.ext as unknown as CodexSessionExt | undefined)?.cliVersion : null;
  return own ?? runtimeOf(machine, agentKindOf(s))?.version ?? null;
}

/**
 * One labelled fact of the header's meta row: a tiny label over its value, the same label
 * style as the four numbers below. Mono values (branch, folder) sit one step smaller.
 */
function MetaFact({ label, children, title, mono }: { label: string; children: ReactNode; title?: string; mono?: boolean }) {
  return (
    <div className={FACT_CELL}>
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className={cn('flex h-5 min-w-0 items-center text-fg-muted', mono ? 'font-mono text-caption' : 'text-ui')} title={title}>
        <span className="min-w-0 truncate">{children}</span>
      </dd>
    </div>
  );
}

/** A count that may be a lower bound: with a tooltip saying so. */
function partialCount(text: string, partial: boolean | undefined) {
  if (!partial) return text;
  return (
    <Tooltip content={PARTIAL_HINT}>
      <span tabIndex={0}>{text}</span>
    </Tooltip>
  );
}

/**
 * Whether the footer carries Claim session or Release claim (SessionClaimButton renders nothing
 * otherwise): on a phone that button is the footer's one labelled action and the rest go to More.
 */
function useClaimShown(s: Session | undefined): boolean {
  const me = useMe();
  const claim = useClaim(s);
  const teams = useTeams();
  const controlled = !!useControlState()?.machines[s?.machineId ?? '']?.installed;
  if (!s) return false;
  const owner = teams.status === 'ready' && teams.data ? (Object.hasOwn(teams.data.sessionOwners, s.id) ? (teams.data.sessionOwners[s.id] ?? '') : '') : null;
  return claimAction({ origin: sessionOrigin(s), claim, owner, controlled, me }).kind !== 'none';
}

function useStopAndTerminal(s: Session | undefined) {
  const machine = useMachine(s?.machineId);
  const [stopOpen, setStopOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const online = !!machine?.online;

  const stop = async () => {
    if (!s) return;
    await runAction(s.machineId, 'session.stop', { sessionId: s.id });
    toast.success('Session stopped', `${clip(s.title, 60)} on ${machine?.name ?? 'its machine'}.`);
  };

  const openTerminal = async () => {
    if (!s) return;
    setOpening(true);
    setError(null);
    try {
      const target = s.checkoutId ? { checkoutId: s.checkoutId } : { cwd: s.cwd };
      const r = await runAction(s.machineId, 'session.start', { ...target, mode: 'terminal', resumeSessionId: s.id, model: s.model ?? undefined, agentKind: agentKindOf(s) });
      toast.success('Terminal opened', r.message ?? `${agentKindLabel(s.agentKind)} is open in ${shortPath(s.cwd, 40)} on ${machine?.name ?? 'its machine'}.`);
    } catch (e) {
      setError(toUiError(e, { what: 'Could not open a terminal for this session.', next: 'Check that the machine is online, then try again.' }));
    } finally {
      setOpening(false);
    }
  };

  const copyResume = async () => {
    if (!s) return;
    const cmd = resumeCommand(s, machine?.os);
    setError(null);
    try {
      await navigator.clipboard.writeText(cmd);
      toast.success('Resume command copied', `Paste it in a terminal on ${machine?.name ?? 'that machine'}.`);
    } catch (e) {
      setError(toUiError(e, { what: 'Could not copy the resume command.', next: `Copy it by hand: ${cmd}` }));
    }
  };

  return { machine, online, stopOpen, setStopOpen, stop, openTerminal, opening, copyResume, error, showError: setError, clearError: () => setError(null) };
}

/** What the session asks, where you can answer it, and the live transcript. */
function ActivityTab({
  s,
  machineName,
  headlessComposer,
  liveTerminal,
  onOpenTerminal,
  onResumeInTerminal,
}: {
  s: Session;
  machineName: string;
  headlessComposer: boolean;
  liveTerminal: boolean;
  onOpenTerminal: () => void;
  /** Resume the session in a terminal window on its machine (the failure card's sign-in fix). */
  onResumeInTerminal: () => Promise<void>;
}) {
  // With session control on its machine, the message box below reaches it too: say so.
  const controlled = !!useControlState()?.machines[s.machineId]?.installed;
  // Elsewhere, a headless session Orgus launched takes messages on its input (session-control module).
  const composer = !controlled && headlessComposer;
  // One fetch for the entry line at the top and the Messages section at the bottom.
  const messages = useMessages(s, composer);
  const block = useApprovalBlock(s);
  const blocked = !!block && block.resumable && block.phase !== 'resuming';
  return (
    <div className="flex flex-col gap-6 px-6 py-6">
      <FailureCard session={s} onOpenTerminal={onResumeInTerminal} />
      {s.status === 'waiting' && (
        <WaitingCallout session={s} machineName={machineName} composer={composer} controlled={controlled} liveTerminal={liveTerminal} onOpenTerminal={onOpenTerminal} />
      )}
      <SessionApprovals sessionIds={[s.id]} />
      <ApprovalBlocker session={s} />
      {composer && s.status !== 'ended' && <MessageEntry session={s} data={messages} blocked={blocked} />}
      <SessionMessage session={s} />
      <ApprovalEvents session={s} />
      <Transcript session={s} />
      {composer && (
        <div className="border-t border-border-subtle pt-4 empty:hidden">
          <Messages session={s} data={messages} />
        </div>
      )}
    </div>
  );
}

/**
 * The header pill. A turn that ended on a question with nothing held reads Asked (neutral), and
 * a waiting session whose ask Orgus cannot show reads Waiting: Needs you only for what blocks it
 * (ADR 0056).
 */
function DrawerStatusPill({ s }: { s: Session }) {
  const v = useAskView(s);
  const level = useSessionNeedsLevel(s.id);
  const unknown = s.status === 'waiting' && !!v.missing && !v.approvalPending;
  const calmLabel = s.status === 'waiting' ? (unknown ? 'Waiting' : pillLabelForLevel(level)) : null;
  const calm = !!calmLabel;
  const label = calmLabel ?? PILL_LABEL[s.status];
  // Asked and open questions wear the question glyph, as in the list's Asked group and the Inbox.
  const question = calmLabel === ASKED_LABEL || calmLabel === OPEN_QUESTION_LABEL;
  return <StatusPill {...sessionStateOfPill(s, question ? 'asked' : calm ? 'idle' : PILL[s.status], label)} />;
}

function AgentsTab({ s }: { s: Session }) {
  const running = s.subagents.filter((a) => a.status === 'running').length + s.workflows.filter((w) => w.status === 'running').length;
  const kind = agentKindOf(s);
  const lanes = agentSupports(kind, 'subagents') || agentSupports(kind, 'workflows');
  const shown = agentsShownNote(s);
  return (
    <div className="flex flex-col gap-3 px-6 py-4">
      <p className="text-caption text-fg-subtle num">
        {!lanes
          ? `${agentKindLabel(kind)} does not report subagents or workflows.`
          : s.subagents.length || s.workflows.length
          ? `${plural(subagentTotal(s), 'subagent')}, ${plural(workflowTotal(s), 'workflow')}${running ? `, ${running} running` : ''}${shown ? `. ${shown}` : ''}`
          : 'This session has not started a subagent or a workflow.'}
      </p>
      <SessionLanes session={s} />
    </div>
  );
}

/**
 * The session's details: its autonomy, then its facts in the drawer's fact grid (the figures the
 * header already shows, tokens, cost and folder, are not repeated), usage, and two folded
 * sections: what Orgus can do with its agent, and its cost per model.
 */
function DetailsTab({ s, machineName, machine }: { s: Session; machineName: string; machine: ReturnType<typeof useMachine> }) {
  const navigate = useNavigate();
  const cost = useSessionCost(s.id);
  const kind = agentKindOf(s);
  const version = agentVersion(s, machine);
  const guess = livenessGuessNote(s, machine?.name);
  const total = cost && cost.total.cost > 0 ? cost.total.cost : 0;
  // The rate the header's cost runs at, once it has run long enough to say (six minutes).
  const hours = sessionRanMs(s, Date.now()) / 3_600_000;
  const rate = total && hours >= 0.1 ? `, ${formatMoneyCompact(total / hours)} an hour` : '';
  const agents = subagentTotal(s);
  const running = s.subagents.filter((a) => a.status === 'running').length;
  const missions = useMissionsState().state?.missions;
  const missionId = missionIdOfTitle(s.title);
  const mission = missionId ? missions?.find((m) => m.id === missionId) : missions?.find((m) => m.sessionIds?.includes(s.id) || m.launches?.some((l) => l.sessionId === s.id));
  const incidentId = (s as unknown as { incidentId?: string }).incidentId ?? mission?.incidentId ?? null;
  return (
    <div className="flex flex-col gap-6 px-6 py-4">
      {incidentId && (
        <div className="flex items-center justify-between rounded-control border border-border-subtle bg-subtle p-3">
          <span className="text-caption text-fg-muted">Fixing incident {incidentId}</span>
          <Button size="sm" variant="secondary" icon={<ArrowUpRight />} onClick={() => navigate(incidentPath(incidentId))}>
            Open incident
          </Button>
        </div>
      )}
      <SessionAutonomy s={s} />
      <DetailSection title="Session" className="border-t border-border-subtle pt-4">
        <FactGrid
          label="Session facts"
          lines={[
            // Three full lines of four, so the columns line up (two to a line on a phone): what ran
            // it, how much it did, what it used. The folder, the one long value, has a line of its own.
            [
              {
                label: 'Agent',
                value: (
                  <>
                    <AgentKindLabel kind={s.agentKind} />
                    {version && <span className="text-fg-subtle">{version}</span>}
                  </>
                ),
              },
              { label: 'Model', value: <SessionModelText session={s} /> },
              { label: 'Started from', value: s.origin === 'orgus' ? 'Orgus' : 'Outside Orgus' },
              { label: 'Last activity', value: <RelativeTime ts={s.lastActivity} /> },
            ],
            [
              { label: 'Turns', value: partialCount(lowerBound(s.turns, s.partial), s.partial) },
              { label: 'Tool calls', value: partialCount(lowerBound(s.toolCalls, s.partial), s.partial) },
              { label: 'Subagents', value: agents ? (running ? `${formatInt(running)} of ${formatInt(agents)} running` : formatInt(agents)) : <NoValue /> },
              { label: 'Process', value: s.pid ? `${s.pid} on ${machineName}` : <NoValue label="No live process" />, title: s.pid ? `${s.pid} on ${machineName}` : undefined },
            ],
            [
              { label: 'Tokens in', value: s.tokens.input > 0 ? formatCompact(s.tokens.input) : <NoValue /> },
              { label: 'Tokens out', value: s.tokens.output > 0 ? formatCompact(s.tokens.output) : <NoValue /> },
              { label: 'Cache reads', value: partialCount(compactLowerBound(s.tokens.cacheRead, s.partial), s.partial) },
              { label: 'Session id', value: s.id.slice(0, 8), title: s.id, mono: true },
            ],
            [
              { label: 'Folder', value: <FolderValue path={s.cwd} local={!!machine?.local} max={64} /> },
              // A guess about whether it is still alive, only when there is one.
              { label: 'State', value: guess ? <span className="min-w-0 truncate" title={guess}>{guess}</span> : null },
              ...(incidentId
                ? [
                    {
                      label: 'Incident',
                      value: (
                        <Button variant="ghost" size="sm" icon={<ArrowUpRight />} onClick={() => navigate(incidentPath(incidentId))}>
                          {incidentId}
                        </Button>
                      ),
                    },
                  ]
                : []),
            ],
          ]}
        />
      </DetailSection>
      <UsageGroup s={s} />
      <DetailSection title={`What Orgus can do with ${agentKindLabel(kind)}`} aside={capabilitySummary(kind)} collapsible className="border-t border-border-subtle pt-4">
        <CapabilityList kind={kind} />
      </DetailSection>
      <DetailSection
        title="Cost per model"
        aside={total ? `${formatMoney(total)}${cost?.source === 'notional' ? ', notional' : ''}${rate}` : s.status === 'ended' ? 'No cost' : 'Nothing yet'}
        collapsible
        className="border-t border-border-subtle pt-4"
      >
        <CostTab session={s} showTokens={false} />
      </DetailSection>
    </div>
  );
}

/**
 * The session detail drawer, opened by `?session=<id>` on whichever page mounts it.
 * `order` is the list the page shows, so j and k move through it.
 */
export function SessionDrawerHost({ order = [] }: { order?: string[] }) {
  const [id, setId] = useSessionParam();
  // ADR 0104: the hub holds a summary; the whole session comes from its machine while the drawer is open.
  const { session: s } = useSessionDetail(useSession(id));
  const { phase } = useConnection();
  // A link (a notification, a shared URL) to a session the chosen workspace hides.
  const hidden = useOutOfScope(!s && id ? { session: id } : null);
  const project = useProject(s?.projectId);
  const checkout = useCheckout(s?.checkoutId);
  const ctl = useStopAndTerminal(s);
  const [tab, setTab] = useTab();
  const [diffMode, setDiffMode] = useDiffMode();
  const { control } = useSessionControl(s);
  const { terms } = useTerms();
  const term = s ? termForSession(terms, s.id) : undefined;
  const termLive = !!term && term.state !== 'exited';
  const idx = id ? order.indexOf(id) : -1;
  const prev = idx > 0 ? order[idx - 1] : undefined;
  const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : undefined;
  const go = (to: string | undefined) => to && setId(to, { replace: true });

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
    const t = e.target as HTMLElement;
    if (t.closest('input, textarea, select, [contenteditable="true"], .xterm')) return;
    if (e.key === 'j') {
      e.preventDefault();
      go(next);
    } else if (e.key === 'k') {
      e.preventDefault();
      go(prev);
    }
  };

  const live = s ? isLive(s) : false;
  const terminalGap = s ? unavailableReason(s.agentKind, 'launch.terminal') : null;
  const stopCap = s ? agentCapabilities(s.agentKind).stop : null;
  // Without a process: say why for a provider that cannot stop what it did not start.
  const noProcess = stopCap && stopCap.support !== 'yes' && stopCap.note ? stopCap.note : 'No process to stop';
  // A session Orgus only watches (discovered, unclaimed, no session control) cannot be stopped from here.
  // Open terminal resumes the session in a terminal on its machine. An ended session resumes
  // there too (Resume in terminal); one still running elsewhere without a process here cannot.
  const resumable = !!s && !s.pid && s.status === 'ended';
  const terminalWhy = terminalGap ?? (!ctl.online ? `${ctl.machine?.name ?? 'The machine'} is offline` : !s?.pid && !resumable ? `No live process here. Copy the resume command (top right) to continue it in your own terminal.` : null);
  const stopGap = !ctl.online ? `${ctl.machine?.name ?? 'The machine'} is offline` : control?.readOnly ? (control.stopBlocked ?? noProcess) : !s?.pid ? noProcess : null;
  const runningChildren = s ? s.subagents.filter((a) => a.status === 'running').length : 0;
  const machineName = ctl.machine?.name ?? 'its machine';
  // The rows the Agents tab lists, so the count matches them (the totals are in the tab's caption).
  const agents = s ? agentRowCount(s) : 0;

  const claimShown = useClaimShown(s);
  const showTerminal = tab !== 'terminal' || termLive;
  const terminalLabel = !termLive && resumable ? 'Resume in terminal' : 'Open terminal';
  const terminalAction = termLive ? () => setTab('terminal') : ctl.openTerminal;
  const shown = useSessionTitle();
  const title = s ? shown(s).title : phase === 'loading' ? 'Loading session' : hidden ? 'Session in another workspace' : 'Session not found';

  return (
    <>
      <Drawer
        open={!!id}
        onOpenChange={(o) => !o && setId(null)}
        title={title}
        width={tab === 'diff' ? drawerWidth('diff', diffMode) : 640}
        // With a session: a fixed top (numbers, tab bar) over the tab's own scroll area.
        bodyClassName={s ? 'flex flex-col overflow-hidden' : undefined}
        onKeyDown={onKeyDown}
        layout="rows"
        eyebrow={
          s && (
            // Its own row beside the icons, never under them: on a narrow screen it wraps to a
            // second line rather than run into them. The block says just Blocked here (its cause
            // is the card's title below, the full label its tooltip).
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              {/* What this drawer is, as a tiny label, like every fact below it. */}
              <span className="shrink-0 text-caption text-fg-subtle">Session</span>
              <DrawerStatusPill s={s} />
              <SessionBlockedBadge session={s} compact />
              <span className="flex min-w-0 items-center">
                <SessionOwner sessionId={s.id} title={s.title} projectId={s.projectId} claimable={false} />
              </span>
              {shown(s).missionId && <MissionChip />}
            </div>
          )
        }
        description={
          s && (
            <>
              {/* Where it runs, in three facts (the folder is on Details); a phone says it in one line. */}
              <dl className={cn(FACT_GRID, checkout?.branch ? 'sm:grid-cols-3' : 'sm:grid-cols-2', 'max-md:hidden')}>
                <MetaFact label="Project" title={project?.name}>
                  {project?.name ?? 'No project'}
                </MetaFact>
                <MetaFact label="Machine" title={ctl.machine?.name}>
                  {ctl.machine?.name ?? 'Unknown machine'}
                </MetaFact>
                {checkout?.branch && (
                  <MetaFact label="Branch" title={checkout.branch} mono>
                    {checkout.branch}
                  </MetaFact>
                )}
              </dl>
              <SessionSummaryLine s={s} project={project?.name ?? 'No project'} machine={ctl.machine?.name ?? 'an unknown machine'} className="md:hidden" />
            </>
          )
        }
        actions={
          s && (
            <>
              <PresenceAvatars view={`session:${s.id}`} />
              {order.length > 1 && (
                <>
                  <IconButton label="Previous session" icon={<ChevronUp />} shortcut={['k']} disabled={!prev} onClick={() => go(prev)} />
                  <IconButton label="Next session" icon={<ChevronDown />} shortcut={['j']} disabled={!next} onClick={() => go(next)} />
                </>
              )}
              <IconButton label="Copy the resume command" icon={<Copy />} onClick={ctl.copyResume} />
            </>
          )
        }
        footer={
          s && (
            <>
              {/* The Terminal tab offers its own way in when there is no shared terminal: one terminal action at a time. */}
              {showTerminal && (
              <Tooltip
                content={
                  termLive
                    ? 'Shows the terminal Orgus started this session in'
                    : (terminalWhy ?? (resumable ? `Continues this session in a new terminal window on ${ctl.machine?.name}` : `Opens ${agentKindLabel(s.agentKind)} in a new terminal window on ${ctl.machine?.name}`))
                }
              >
                {/* On a phone with Claim session in the footer, this moves into More. */}
                <span tabIndex={termLive || !terminalWhy ? -1 : 0} className={cn(claimShown && 'max-sm:hidden')}>
                  <Button icon={<SquareTerminal />} loading={ctl.opening} disabled={!termLive && !!terminalWhy} onClick={terminalAction}>
                    {terminalLabel}
                  </Button>
                </span>
              </Tooltip>
              )}
              <SessionClaimButton s={s} onError={ctl.showError} />
              {live && (
                <Tooltip content={stopGap}>
                  <span tabIndex={stopGap ? 0 : -1} className="ml-auto max-sm:hidden">
                    <Button variant="danger" icon={<Octagon />} disabled={!!stopGap} onClick={() => ctl.setStopOpen(true)}>
                      Stop session
                    </Button>
                  </span>
                </Tooltip>
              )}
              {/* A phone keeps one labelled action in the footer; the others, Stop session last, are in More. */}
              {(live || (showTerminal && claimShown)) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button iconRight={<ChevronDown />} className="ml-auto sm:hidden">
                      More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {showTerminal && claimShown && (
                      <DropdownMenuItem icon={<SquareTerminal />} disabled={!termLive && !!terminalWhy} onSelect={terminalAction}>
                        {terminalLabel}
                      </DropdownMenuItem>
                    )}
                    {showTerminal && claimShown && live && <DropdownMenuSeparator />}
                    {live && (
                      <DropdownMenuItem icon={<Octagon />} danger disabled={!!stopGap} onSelect={() => ctl.setStopOpen(true)}>
                        Stop session
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )
        }
      >
        {s ? (
          <>
            {ctl.error && (
              <div className="shrink-0 px-6 pt-4">
                <ErrorState variant="inline" error={ctl.error} action={<Button variant="ghost" size="sm" onClick={ctl.clearError}>Dismiss</Button>} />
              </div>
            )}
            <SessionFacts s={s} />
            <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex min-h-0 flex-1 flex-col gap-0">
              {/* On a phone the three lesser tabs fold into More (the open one stays in the row), and
                  the icons give way to the labels, so the row fits without scrolling. */}
              <div className="flex shrink-0 items-center gap-4 border-b border-border-subtle px-6">
                <TabsList className="min-w-0 flex-1 border-b-0" aria-label="Session views">
                  <TabsTrigger value="activity" icon={<Activity className="max-sm:hidden" />}>
                    Activity
                  </TabsTrigger>
                  <TabsTrigger value="diff" icon={<FileDiff className="max-sm:hidden" />}>
                    Diff
                  </TabsTrigger>
                  <TabsTrigger value="events" icon={<ListTree className="max-sm:hidden" />}>
                    Events
                  </TabsTrigger>
                  <TabsTrigger value="agents" icon={<Bot className="max-sm:hidden" />} count={agents || undefined} className={folded(tab, 'agents')}>
                    Agents
                  </TabsTrigger>
                  <TabsTrigger value="details" icon={<FileText className="max-sm:hidden" />} className={folded(tab, 'details')}>
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="terminal" icon={<SquareTerminal className="max-sm:hidden" />} className={folded(tab, 'terminal')}>
                    Terminal
                    {termLive && <StatusDot tone={term.lock ? 'waiting' : 'working'} size="xs" label={term.lock ? `${term.lock.label} is typing` : 'Live terminal'} />}
                  </TabsTrigger>
                </TabsList>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" iconRight={<ChevronDown />} className="shrink-0 text-fg-subtle sm:hidden">
                      More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {FOLDED.filter((t) => t !== tab).map((t) => (
                      <DropdownMenuItem key={t} icon={FOLDED_ICON[t]} onSelect={() => setTab(t)}>
                        {FOLDED_LABEL[t]}
                        {t === 'agents' && agents > 0 && <span className="ml-auto text-fg-subtle num">{formatInt(agents)}</span>}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <TabsContent value="activity" className={TAB_SCROLL}>
                <ActivityTab s={s} machineName={machineName} headlessComposer={!!control?.showComposer} liveTerminal={termLive} onOpenTerminal={() => setTab('terminal')} onResumeInTerminal={ctl.openTerminal} />
              </TabsContent>
              <TabsContent value="diff" className={cn(TAB_SCROLL, 'px-6 py-4')}>
                <DiffTab session={s} mode={diffMode} onModeChange={setDiffMode} />
              </TabsContent>
              <TabsContent value="events" className={cn(TAB_SCROLL, 'px-6 py-4')}>
                <EventsTab session={s} live={live} />
              </TabsContent>
              <TabsContent value="agents" className={TAB_SCROLL}>
                <AgentsTab s={s} />
              </TabsContent>
              <TabsContent value="details" className={TAB_SCROLL}>
                <DetailsTab s={s} machineName={machineName} machine={ctl.machine} />
              </TabsContent>
              <TabsContent value="terminal" className={cn(TAB_SCROLL, 'px-6 py-4')}>
                <SessionTerminalTab session={s} onAnswer={() => setTab('activity')} />
              </TabsContent>
            </Tabs>
          </>
        ) : hidden ? (
          <OutOfScopeState what="session" info={hidden} />
        ) : phase === 'loading' ? (
          <div className="flex flex-col gap-6 px-6 py-4" role="status" aria-label="Loading">
            <SkeletonText lines={2} />
            <SkeletonText lines={4} />
          </div>
        ) : (
          <EmptyState
            icon={<SearchX />}
            title="This session is not here"
            description="It may be older than 7 days, or its machine has not reported it yet."
            action={<Button onClick={() => setId(null)}>Close</Button>}
          />
        )}
      </Drawer>
      {s && (
        <ConfirmDialog
          open={ctl.stopOpen}
          onOpenChange={ctl.setStopOpen}
          title={`Stop "${clip(s.title, 56)}"?`}
          description={
            <>
              This ends process <span className="font-medium text-fg num">{s.pid}</span> on <span className="font-medium text-fg">{machineName}</span>
              {runningChildren ? `, and its ${plural(runningChildren, 'running subagent')} stop with it` : ''}. The transcript stays, so you can resume it
              later.
            </>
          }
          confirmLabel="Stop session"
          onConfirm={ctl.stop}
        />
      )}
    </>
  );
}
